export const getBaseUrl = () => {
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";
    return apiUrl.replace("/api/graphql", "");
};

function resolveAssetUrl(url: string): string {
    if (url.startsWith("http://") || url.startsWith("https://")) {
        return url;
    }

    const path = url.startsWith("/") ? url : `/${url}`;

    // In local dev, CRA proxies /assets to the backend (see setupProxy.js).
    if (process.env.NODE_ENV === "development") {
        return path;
    }

    const baseUrl = getBaseUrl().replace(/\/$/, "");
    return `${baseUrl}${path}`;
}

export const getFileUrl = (url?: string | null) => {
    if (!url) return null;
    return resolveAssetUrl(url);
};

export async function fetchFileBlob(url: string): Promise<Blob> {
    const absoluteUrl = resolveAssetUrl(url);
    const response = await fetch(absoluteUrl, {
        credentials: "include",
        mode: "cors",
    });

    if (!response.ok) {
        if (response.status === 404) {
            throw new Error("CV file not found on the server.");
        }
        throw new Error(`Failed to load CV (HTTP ${response.status}).`);
    }

    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("text/html")) {
        throw new Error(
            "CV file is not available. The record exists but the PDF is missing from the server, or the file URL redirected to a login page."
        );
    }

    return response.blob();
}

export async function openFileInNewTab(url: string): Promise<void> {
    const blob = await fetchFileBlob(url);
    const blobUrl = URL.createObjectURL(blob);

    const newWindow = window.open("", "_blank", "noopener,noreferrer");
    if (!newWindow) {
        URL.revokeObjectURL(blobUrl);
        throw new Error("Popup blocked. Please allow popups for this site.");
    }

    newWindow.document.title = "CV";
    newWindow.document.body.style.margin = "0";
    const iframe = newWindow.document.createElement("iframe");
    iframe.src = blobUrl;
    iframe.style.cssText = "width:100%;height:100vh;border:none;";
    newWindow.document.body.appendChild(iframe);

    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

export function suggestDownloadFilename(displayName: string, url: string): string {
    try {
        const pathname = new URL(url, "http://localhost").pathname;
        const base = pathname.split("/").pop();
        if (base && base.includes(".")) {
            return decodeURIComponent(base);
        }
    } catch {
    }
    const safeName =
        displayName.replace(/[^\w\-]+/g, "_").replace(/_+/g, "_") || "talent";
    return `${safeName}_CV.pdf`;
}

export async function downloadFileFromUrl(
    url: string,
    filename?: string
): Promise<void> {
    const blob = await fetchFileBlob(url);
    const blobUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = filename || suggestDownloadFilename("download", url);
    document.body.appendChild(anchor);
    anchor.click();

    setTimeout(() => {
        document.body.removeChild(anchor);
        URL.revokeObjectURL(blobUrl);
    }, 500);
}

export const getPlayStoreUrl = () => {
    return process.env.REACT_APP_MOBILE_APP_PLAYSTORE_URL || ""
}
